import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { financialAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import { fmtDate } from '../utils/date';
import { PageError, loadError } from '../components/OfflineNotice';

const TITLES = {
  revenue:     'Revenue',
  cogs:        'Cost of Goods Sold',
  expenses:    'Expenses',
  receivables: 'Receivables',
  stock:       'Stock Value',
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function FinancialDetail() {
  const { type }            = useParams();
  const [searchParams]      = useSearchParams();
  const { business }        = useAuth();
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  const year  = searchParams.get('year')  || '';
  const month = searchParams.get('month') || '';
  const currency = business?.currency || '';

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (year)  params.year  = year;
    if (month) params.month = month;
    financialAPI.detail(type, params)
      .then(r => setRows(r.data.rows || []))
      .catch((err) => setError(loadError(err, 'Failed to load records')))
      .finally(() => setLoading(false));
  }, [type, year, month]);

  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);


  const periodLabel = () => {
    if (!year) return 'All time';
    if (month) return `${MONTHS[parseInt(month) - 1]} ${year}`;
    return year;
  };

  const renderRow = (r) => {
    let top, bottom, amount, amountColor, badge;

    if (type === 'revenue') {
      top    = r.product_name || '—';
      bottom = `${r.buyer_name} · ${fmtDate(r.sold_at)}`;
      amount = r.total_amount;
      badge  = r.payment_status;
    } else if (type === 'cogs') {
      // Cost of each item SOLD in the period, at its cost when it was sold.
      top    = r.product_name || '—';
      bottom = `Sold ${fmtDate(r.sold_at)} · Qty ${Number(r.qty).toFixed(3)} × ${currency} ${fmt(r.unit_cost)} cost`;
      amount = r.total_cost;
    } else if (type === 'expenses') {
      top    = r.category;
      bottom = `${fmtDate(r.expense_at)}${r.description ? ` · ${r.description}` : ''}`;
      amount = r.amount;
    } else if (type === 'receivables') {
      top    = r.buyer_name;
      bottom = `${r.product_name} · ${fmtDate(r.sold_at)}${r.due_date ? ` · Due ${fmtDate(r.due_date)}` : ''}`;
      amount = r.outstanding;
      amountColor = 'var(--color-warning)';
    } else if (type === 'stock') {
      top    = r.name;
      bottom = `Qty ${Number(r.stock_qty).toFixed(3)} × ${currency} ${fmt(r.unit_cost)} average cost`;
      amount = r.stock_value;
    }

    return (
      <div key={r.id} className="card" style={{ marginBottom: '0.65rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{top}</div>
            <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: '0.2rem' }}>{bottom}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem', flexShrink: 0 }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: amountColor || 'inherit' }}>
              {currency} {fmt(amount)}
            </span>
            {badge && (
              <span className={`badge badge-${badge}`} style={{ fontSize: '0.65rem' }}>{badge}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">{TITLES[type] || 'Detail'}</h1>
      <p className="text-muted" style={{ marginBottom: '1.25rem', fontSize: '0.85rem' }}>
        {currency} · {periodLabel()}
      </p>

      {loading && <p className="text-muted">Loading…</p>}
      {error   && <PageError error={error} />}
      {!loading && !error && rows.length === 0 && (
        <p className="text-muted">No records for this period.</p>
      )}
      {rows.map(r => renderRow(r))}
    </div>
  );
}
